import Cookies from 'js-cookie';

import config from '@/config';
import { FunctionalitiesEnum } from '@/config/types';
import {
  filterCloseLiveTableGames,
  transformImage,
} from '@/model/algolia/utils';
import {
  AvailableGridTypes,
  CategoriesTypes,
  CategoriesWithContentTypes,
  CategoryBannerType,
  CategoryBannerWithContentType,
  CategoryBaseWithContentType,
  CategoryFinancialWebAttributes,
  CategoryGameCollectionType,
  CategoryGameCollectionWithContentType,
  CategoryGameGridType,
  CategoryGameGridWithContentType,
  CategoryJackpotGridType,
  CategoryJackpotGridWithContentType,
  CategoryShuffleGameGridType,
  CategoryShuffleGameGridWithContentType,
} from '@/types/category/categoryTypes';
import { VariantValues } from '@/types/environment';
import {
  CloseStrategyEnum,
  CloseStrategyType,
  GameCloseStrategies,
  PortalVerticalGameTilePropTypes,
} from '@/types/gameTypes';
import {
  GamesGlobalJackpotV2Values,
  JackpotSlotMapped,
  JackpotsSimplifiedType,
  JackpotsV2StateValues,
  MultipleJackpotV2Interface,
  SingleJackpotV2Interface,
} from '@/types/jackpotTypes';
import { ShuffleWidgetMapped } from '@/types/shuffleWidgetTypes';
import { StrikeGoldSlotMapped } from '@/types/strikeGoldTypes';
import { getJackpotPropertiesFromJackpotTile } from '@/utils/jackpotsV2/getJackpotPropertiesFromJackpotTile';
import get, { has, isEqual } from '@/utils/lodash-alternatives';
import { getShuffleGamesProperties } from '@/utils/shuffleGames/getShuffleGamesProperties';
import { getStrikeGoldProperties } from '@/utils/strikeGold/getStrikeGoldProperties';
import { StrikeGoldData } from '@/utils/strikeGold/types';

import {
  CHUNK_SIZE_MAP,
  CONTEXT_ATTRIBUTES,
  GAMING_SEGMENTS_COOKIE_NAME,
  MAXIMUM_BANNER_SIZE,
  WEB_ATTRIBUTES,
} from './constants';
import {
  ControllersDataItem,
  DeviceOs,
  DeviceType,
  LogicContext,
  PartialEventData,
} from './types';

export const isIframeWrapperEnabled = (): boolean =>
  !!(config.features['iFrame'] && config.features['iFrame'].mode === 'enabled');

export const isModalForMobileEnabled = !!(
  config.features.modalForMobile?.mode === 'enabled'
);

export const isTrackingDisabled = () =>
  !config.features.tracking || config.features.tracking.mode === 'disabled';

export const isTrackingDelayed = () => {
  return config.features.tracking && config.features.tracking.delayed;
};

export const managePhrases = (
  oldPhrases: string[],
  newPhrase: string | string[],
  length: number
) => {
  let finalPhrases: string[] = [];
  if (typeof newPhrase === 'string') {
    finalPhrases = oldPhrases
      .filter(phrase => phrase !== newPhrase)
      .slice(0, length - 1);
    finalPhrases.unshift(newPhrase);
  } else {
    finalPhrases = oldPhrases;
    const cutted = newPhrase.slice(0, length - 1).reverse();
    cutted.forEach(phrase => {
      if (!finalPhrases.includes(phrase)) {
        finalPhrases.unshift(phrase);
      }
    });
    finalPhrases = finalPhrases.slice(0, length);
  }
  return finalPhrases;
};

export const groupItemsByTypes = (
  items: Array<PortalVerticalGameTilePropTypes>
) => {
  const grouppedItems: {
    [key: string]: Array<PortalVerticalGameTilePropTypes>;
  } = {
    All: items,
  };
  items.forEach(game => {
    if (game.type) {
      if (grouppedItems[game.type]) {
        grouppedItems[game.type].push(game);
      } else {
        grouppedItems[game.type] = [game];
      }
    }
  });
  return grouppedItems;
};

export const checkIfAllLiveGamesAreClosed = (
  games: PortalVerticalGameTilePropTypes[] = []
) =>
  !!games.every(
    (game: PortalVerticalGameTilePropTypes) =>
      game.table_id && game.table?.online.open === false
  );

const parseTimeToMinutes = (startTime?: string) => {
  if (!startTime) return 0;

  const times = startTime.split(':').map(Number);
  return times[0] * 60 + times[1];
};

export const getStartTimeTables = (
  games: PortalVerticalGameTilePropTypes[] = []
) => {
  const startTimes = games
    .map(game => game.table?.online)
    .filter(tableOnline => tableOnline?.open === false)
    .map(tableOnline => tableOnline?.startTime);
  if (!startTimes) return undefined;
  if (startTimes.length === 1) {
    return startTimes[0];
  } else {
    startTimes.sort((a, b) => parseTimeToMinutes(a) - parseTimeToMinutes(b));
  }

  return startTimes[0];
};

function Banner({
  category,
  windowInnerWidth,
  currentPersonalizedBanners,
}: {
  category: CategoryBannerType;
  windowInnerWidth: number;
  currentPersonalizedBanners?: LogicContext['currentPersonalizedBanners'];
}): CategoryBannerWithContentType {
  return {
    ...category,
    slides: category.bannerSlides.map(slide => ({
      type: 'Banner' as 'Banner',
      gridType: 'Banner',
      banner: {
        asset: slide.banner.asset && {
          ...slide.banner.asset,
          source: transformImage(
            slide.banner.asset.source,
            Math.min(MAXIMUM_BANNER_SIZE, windowInnerWidth)
          ),
        },
        smallLargeAsset: slide.banner.smallLargeAsset && {
          ...slide.banner.smallLargeAsset,
          source: transformImage(
            slide.banner.smallLargeAsset.source,
            Math.min(MAXIMUM_BANNER_SIZE, windowInnerWidth)
          ),
        },
        mediumAsset: slide.banner.mediumAsset && {
          ...slide.banner.mediumAsset,
          source: transformImage(
            slide.banner.mediumAsset.source,
            Math.min(MAXIMUM_BANNER_SIZE, windowInnerWidth)
          ),
        },
        buttonText: slide.banner.buttonText,
        buttonPosition: slide.banner.buttonPosition,
        description: slide.banner.description,
        type: slide.banner.type,
        game: slide.banner.game,
        personalization: slide.banner.personalization && {
          ...slide.banner.personalization,
          rendered:
            currentPersonalizedBanners &&
            currentPersonalizedBanners[slide.banner.personalization.selector],
        },
        route_link: slide.banner.route_link,
        link: slide.banner.link,
        notIndexed: slide.banner.notIndexed,
      },
    })),
  };
}

function getChunkMap(slideType: AvailableGridTypes) {
  return CHUNK_SIZE_MAP[slideType] || CHUNK_SIZE_MAP['Grid'];
}

export const divideGameListToSlides = ({
  gameList = [],
  slideType = 'Grid',
  widgetSlot,
  unlimited,
}: {
  gameList?: PortalVerticalGameTilePropTypes[];
  slideType: AvailableGridTypes;
  widgetSlot?: JackpotSlotMapped | StrikeGoldSlotMapped | ShuffleWidgetMapped;
  unlimited?: boolean;
}): CategoryBaseWithContentType['slides'] => {
  let chunkSize = getChunkMap(slideType).normal;
  const slides: {
    widgetSlot?: JackpotSlotMapped | StrikeGoldSlotMapped | ShuffleWidgetMapped;
    gameList: PortalVerticalGameTilePropTypes[];
    type: AvailableGridTypes;
    gamesLimit?: number;
    slideInitialIndex: number;
  }[] = [];
  let slideInitialIndex = 0;

  for (let i = 0, j = gameList.length; i < j; i += chunkSize) {
    chunkSize = getChunkMap(slideType).normal;
    switch (slideType) {
      case 'Jackpot Hero':
      case 'Jackpot Banner':
      case 'Game Shuffle Hero':
      case 'Widget Hero':
      case 'Widget Banner': {
        slides.push({
          type: slideType,
          gameList: gameList.slice(
            i,
            i === 0 ? getChunkMap(slideType).big : i + chunkSize
          ),
          gamesLimit:
            i === 0
              ? getChunkMap(slideType).big
              : getChunkMap(slideType).normal,
          widgetSlot,
          slideInitialIndex,
        });
        slideType =
          slideType === 'Widget Banner' || slideType === 'Jackpot Banner'
            ? 'Game Dual'
            : 'Jackpot Nine';
        break;
      }

      case 'Game Banner': {
        if (!gameList[i].bannerImage) {
          const fallbackType = 'Game Dual';
          chunkSize = getChunkMap(fallbackType).normal;
          slides.push({
            type: 'Game Dual',
            gameList: gameList.slice(
              i,
              i === 0 ? getChunkMap(fallbackType).big : i + chunkSize
            ),
            gamesLimit:
              i === 0
                ? getChunkMap(fallbackType).big
                : getChunkMap(fallbackType).normal,
            slideInitialIndex,
          });
        } else {
          slides.push({
            type: slideType,
            gameList: gameList.slice(
              i,
              i === 0 ? getChunkMap(slideType).big : i + chunkSize
            ),
            gamesLimit:
              i === 0
                ? getChunkMap(slideType).big
                : getChunkMap(slideType).normal,
            slideInitialIndex,
          });
        }
        break;
      }
      case 'Vertical Horizontal':
      case 'Dual Vertical Horizontal':
      case 'Horizontal Vertical':
      case 'Dual Horizontal Vertical':
      case 'Top Games':
      case 'Tall Single Block':
      case 'Dynamic Grid':
      case 'Collection': {
        slides.push({
          type: slideType,
          gameList: gameList,
          gamesLimit: gameList.length,
          slideInitialIndex,
        });
        chunkSize = gameList.length;
        break;
      }
      default: {
        const chunkMap = getChunkMap(slideType);
        slides.push({
          type: slideType,
          gameList: gameList.slice(i, i === 0 ? chunkMap.big : i + chunkSize),
          gamesLimit: unlimited
            ? undefined
            : i === 0
            ? chunkMap.big
            : chunkMap.normal,
          slideInitialIndex,
        });
        break;
      }
    }
    slideInitialIndex += chunkSize;
  }

  return slides;
};

export function mapGamesWithTables(
  games: PortalVerticalGameTilePropTypes[],
  currentTables: LogicContext['currentTables'],
  locale?: string,
  currency?: string
) {
  if (currentTables && games) {
    return games.map((game: any) => {
      if (game.table_id && currentTables[game.table_id]) {
        return {
          ...game,
          table: {
            ...currentTables[game.table_id],
            locale,
            currencyCode: currency,
          },
        };
      }
      return game;
    });
  }

  return games;
}

const isJackpotV2Valid = (
  jackpotData: Pick<SingleJackpotV2Interface, 'value' | 'currency'>
) =>
  !!jackpotData &&
  jackpotData.value?.state !== JackpotsV2StateValues.WON &&
  !!jackpotData.value?.price &&
  !!jackpotData.currency;

const jackpotToSingle = (
  jackpot: SingleJackpotV2Interface | MultipleJackpotV2Interface,
  provider: string
): SingleJackpotV2Interface | null => {
  if (!jackpot || isEmptyObject(jackpot)) {
    return null;
  }

  if (isSingleJackpot(jackpot)) {
    return jackpot;
  }

  if (provider === 'Games Global') {
    return {
      code: jackpot.code,
      currency: jackpot.currency,
      timestamp: jackpot.timestamp,
      value: jackpot.values[GamesGlobalJackpotV2Values.MEGA],
    };
  }

  throw Error('Passed jackpot is not supported');
};

export function mapGamesWithJackpotsV2(
  games: PortalVerticalGameTilePropTypes[],
  currentJackpotsV2?: LogicContext['currentJackpotsV2']
) {
  if (!currentJackpotsV2 || !Object.keys(currentJackpotsV2).length || !games) {
    return games;
  }
  return games.map((game: PortalVerticalGameTilePropTypes) => {
    if (!game.jackpotCode || !currentJackpotsV2[game.provider]) {
      return game;
    }

    const jackpot = jackpotToSingle(
      currentJackpotsV2[game.provider][game.jackpotCode],
      game.provider
    );

    if (!jackpot || !isJackpotV2Valid(jackpot)) {
      return game;
    }

    const gameWithJackpot = {
      ...game,
      jackpot: {
        currencyCode: jackpot.currency,
        value: jackpot.value.price,
        timestamp: Date.parse(jackpot.timestamp),
        increaseVelocity: game.jackpotVelocity || 0,
        updateInterval: jackpot.value.updateInterval,
      },
    };
    return gameWithJackpot;
  });
}

export const isSingleJackpot = (
  jackpot: SingleJackpotV2Interface | MultipleJackpotV2Interface
): jackpot is SingleJackpotV2Interface => 'value' in jackpot;

export const mergeObjectDepthOne = <T extends Record<string, any>>(
  objectToMerge: T,
  data: T
): T => {
  const merged = Object.entries(data || {}).reduce(
    (mergedValues, [provider, multiJackpot]) => {
      mergedValues[provider as keyof T] = {
        ...objectToMerge?.[provider],
        ...multiJackpot,
      };
      return mergedValues;
    },
    {} as T
  );
  return { ...objectToMerge, ...merged };
};

const prepareCategory = ({
  category,
  multiJackpots,
  currentJackpotsV2,
  currentTables,
  currentPersonalizedBanners,
  shuffleGame,
  windowInnerWidth,
  locale,
  currency,
  gameDetailsInfo,
  strikeGoldData,
}: {
  category: CategoriesTypes;
  multiJackpots?: LogicContext['multiJackpots'];
  currentJackpotsV2?: LogicContext['currentJackpotsV2'];
  currentTables?: LogicContext['currentTables'];
  currentPersonalizedBanners?: LogicContext['currentPersonalizedBanners'];
  shuffleGame?: any;
  windowInnerWidth: number;
  locale?: string;
  currency?: string;
  gameDetailsInfo: boolean;
  strikeGoldData: StrikeGoldData;
}): CategoriesWithContentTypes | null => {
  switch (category.component_type) {
    case 'banner':
      return prepareBanner({
        category,
        windowInnerWidth,
        currentPersonalizedBanners,
        gameDetailsInfo,
      });
    case 'game_collection': {
      return prepareGameCollection({
        category,
        currency,
        currentJackpotsV2,
        currentTables,
        windowInnerWidth,
        locale,
        gameDetailsInfo,
      });
    }
    default:
      return prepareGameGrid({
        category,
        currency,
        multiJackpots,
        currentJackpotsV2,
        currentTables,
        shuffleGame,
        windowInnerWidth,
        locale,
        gameDetailsInfo,
        strikeGoldData,
      });
  }
};

const prepareBanner = ({
  category,
  currentPersonalizedBanners,
  windowInnerWidth,
  gameDetailsInfo,
}: {
  category: CategoryBannerType;
  currentPersonalizedBanners?: LogicContext['currentPersonalizedBanners'];
  windowInnerWidth: number;
  gameDetailsInfo: boolean;
}): CategoryBannerWithContentType => ({
  ...Banner({ category, windowInnerWidth, currentPersonalizedBanners }),
  gameDetailsInfo,
});

const prepareGameCollection = ({
  category,
  currentJackpotsV2,
  currentTables,
  windowInnerWidth,
  locale,
  currency,
  gameDetailsInfo,
}: {
  category: CategoryGameGridType | CategoryGameCollectionType;
  currentJackpotsV2?: LogicContext['currentJackpotsV2'];
  currentTables?: LogicContext['currentTables'];
  windowInnerWidth: number;
  locale?: string;
  currency?: string;
  gameDetailsInfo: boolean;
}): CategoryGameGridWithContentType | CategoryGameCollectionWithContentType => {
  let { games, overlay, time } = prepareGames({
    games: category.games,
    currentJackpotsV2,
    currentTables,
    locale,
    currency,
    windowInnerWidth,
  });

  const preparedGridCategpry = {
    ...category,
    overlay,
    tableStartTime: time,
    gameDetailsInfo,
    slides: divideGameListToSlides({
      gameList: games,
      slideType: 'Collection',
    }),
  };

  return preparedGridCategpry;
};

const prepareGameGrid = ({
  category,
  multiJackpots,
  currentJackpotsV2,
  currentTables,
  shuffleGame,
  windowInnerWidth,
  locale,
  currency,
  gameDetailsInfo,
  strikeGoldData,
}: {
  category:
    | CategoryGameGridType
    | CategoryGameCollectionType
    | CategoryJackpotGridType
    | CategoryShuffleGameGridType;
  multiJackpots?: LogicContext['multiJackpots'];
  currentJackpotsV2?: LogicContext['currentJackpotsV2'];
  currentTables?: LogicContext['currentTables'];
  shuffleGame?: any;
  windowInnerWidth: number;
  locale?: string;
  currency?: string;
  gameDetailsInfo: boolean;
  strikeGoldData: StrikeGoldData;
}):
  | CategoryGameGridWithContentType
  | CategoryGameCollectionWithContentType
  | CategoryJackpotGridWithContentType
  | CategoryShuffleGameGridWithContentType
  | null => {
  const { games, overlay, time } = prepareGames({
    games: category.games,
    currentJackpotsV2,
    currentTables,
    locale,
    currency,
    windowInnerWidth,
  });

  let jackpotSlot;
  let customTileSlot;
  let gameShuffleSlot;

  if (category.component_type === 'jackpot_grid') {
    jackpotSlot = getJackpotPropertiesFromJackpotTile({
      jackpotTile: category.jackpot_tile,
      multiJackpots: multiJackpots?.[category.jackpot_tile?.provider || ''],
      currency,
      locale,
    });

    if (!jackpotSlot) {
      return null;
    }
  }

  if (
    category.component_type === 'game_grid' &&
    category.custom_widget_tile &&
    category.custom_widget_tile.type === 'strike-gold'
  ) {
    customTileSlot = getStrikeGoldProperties({
      strikeGoldData,
      strikeGoldTile: category.custom_widget_tile,
      currency,
      locale,
    });
  }

  if (
    category.component_type === 'game_shuffle_grid' &&
    category.game_shuffle_tile &&
    category.game_shuffle_tile.type === 'shuffle-games'
  ) {
    gameShuffleSlot = getShuffleGamesProperties({
      shuffleGamesTile: category.game_shuffle_tile,
      game: shuffleGame,
    });
  }

  let widgetSlot = jackpotSlot || customTileSlot || gameShuffleSlot;

  const preparedCategory = {
    ...category,
    overlay,
    tableStartTime: time,
    gameDetailsInfo,
    slides:
      !games.length && widgetSlot
        ? [
            {
              widgetSlot,
              type: category.type,
              slideInitialIndex: 0,
            },
          ]
        : divideGameListToSlides({
            gameList: games,
            slideType: category.type,
            widgetSlot,
          }),
  };

  return preparedCategory;
};

export function getCategory({
  category,
  multiJackpots,
  currentJackpotsV2,
  currentTables,
  currentPersonalizedBanners,
  shuffleGame,
  windowInnerWidth = 400,
  locale,
  currency,
  strikeGoldData,
}: {
  category: CategoriesTypes;
  multiJackpots?: LogicContext['multiJackpots'];
  currentJackpotsV2?: LogicContext['currentJackpotsV2'];
  currentTables?: LogicContext['currentTables'];
  currentPersonalizedBanners?: LogicContext['currentPersonalizedBanners'];
  shuffleGame: PortalVerticalGameTilePropTypes | null;
  windowInnerWidth?: number;
  locale?: string;
  currency?: string;
  strikeGoldData: StrikeGoldData;
}): CategoriesWithContentTypes | null {
  return prepareCategory({
    category,
    currency,
    multiJackpots,
    currentJackpotsV2,
    currentTables,
    currentPersonalizedBanners,
    shuffleGame,
    windowInnerWidth,
    locale,
    gameDetailsInfo: config.features.gameDetails.mode === 'enabled',
    strikeGoldData,
  });
}

const prepareGames = ({
  games,
  currentJackpotsV2,
  currentTables,
  locale,
  currency,
  windowInnerWidth,
}: {
  games: PortalVerticalGameTilePropTypes[];
  currentJackpotsV2?: LogicContext['currentJackpotsV2'];
  currentTables?: LogicContext['currentTables'];
  windowInnerWidth: number;
  locale?: string;
  currency?: string;
}) => {
  let gamesWithJackpotV2:
    | PortalVerticalGameTilePropTypes[]
    | null = mapGamesWithJackpotsV2(games, currentJackpotsV2);
  let gamesWithTables:
    | PortalVerticalGameTilePropTypes[]
    | null = mapGamesWithTables(
    gamesWithJackpotV2,
    currentTables,
    locale,
    currency
  );

  let filteredGames: PortalVerticalGameTilePropTypes[] = filterCloseLiveTableGames(
    gamesWithTables
  );

  gamesWithJackpotV2 = null;
  gamesWithTables = null;

  return {
    games: filteredGames,
    overlay: checkIfAllLiveGamesAreClosed(filteredGames),
    time: getStartTimeTables(filteredGames),
  };
};

export function removeQueryParams(url: string) {
  return url.split('?')[0];
}

export function getPreviousPageArray<T extends { pagePath: string }>({
  pageParams,
  previousPageArray = [],
  arraySizeLimit,
}: {
  pageParams: T;
  previousPageArray: T[];
  arraySizeLimit: number;
}): T[] {
  const pageIdArray = previousPageArray.slice(-arraySizeLimit);
  return pageIdArray.slice(-1)[0]?.pagePath === pageParams.pagePath
    ? pageIdArray
    : [...pageIdArray, pageParams];
}

export function getPromotionsPath(promotionSlug?: string): string {
  const basePath = config.base_path || '';
  const promoBasePath = `${basePath}${getSpecialRoutePath(
    FunctionalitiesEnum.promotions
  )}`;
  //update promotions with current slug if passed
  return promotionSlug ? `${promoBasePath}/${promotionSlug}` : promoBasePath;
}

export function getProductContext(): string | null {
  return config.features.tracking.productContext;
}

//tmp solution for app specific params

const createUrlParams = (params: any) => {
  const urlParams = new URLSearchParams();
  Object.keys(params).forEach((key: string) => {
    const param = params[key];
    if (param) {
      const mappedKey = WEB_ATTRIBUTES.includes(key) ? `web.${key}` : key;

      if (Array.isArray(param)) {
        urlParams.append(`context[${mappedKey}]`, param.toString());
      } else {
        const paramKey = CONTEXT_ATTRIBUTES.includes(key)
          ? `context[${mappedKey}]`
          : mappedKey;

        urlParams.append(paramKey, param);
      }
    }
  });

  urlParams.sort();
  return urlParams.toString();
};

const mapUserDevice = (deviceType: string, deviceOs: string | null) => {
  if (deviceType === 'desktop') return 'web';
  else {
    if (deviceOs === 'android' || deviceOs === 'ios') return deviceOs;
    return 'mobile_web';
  }
};

const getSegmentsCookie = () => {
  try {
    const cookie = Cookies.get(GAMING_SEGMENTS_COOKIE_NAME);
    const segments = cookie ? JSON.parse(cookie)?.segments || [] : [];
    return segments;
  } catch (e) {
    console.error(`Could not parse cookie "${GAMING_SEGMENTS_COOKIE_NAME}".`);
    return [];
  }
};

export function mapUrl(
  url: string,
  deviceType: DeviceType,
  deviceOs: DeviceOs,
  categoryAttributes: CategoryFinancialWebAttributes = {}
) {
  const specificParams = createUrlParams({
    ...config.launcher.staticUrlParams,
    agent: mapUserDevice(deviceType, deviceOs),
    segments: getSegmentsCookie(),
    game_sub_category: categoryAttributes.title,
    game_top_category: categoryAttributes.topCategory,
  });

  return `${config.launcher.apiUrl}${url}&${specificParams}&channel=${deviceType}`;
}

export function pathJoin(parts: Array<string | undefined>) {
  const replace = new RegExp('([^:])/{1,}', 'g');
  return parts
    .filter(path => !!path)
    .join('/')
    .replace(replace, '$1/');
}

export function createSearchQuery(params?: {
  [key: string]: string | number | boolean | string[] | undefined;
}): string {
  if (!params) return '';

  const urlParams = new URLSearchParams();
  Object.keys(params).forEach((key: string) => {
    const param = params[key];
    if (param !== undefined) {
      if (Array.isArray(param)) {
        param.forEach((paramItem: string) =>
          urlParams.append(`${key}[]`, paramItem)
        );
        if (param.length === 0) {
          urlParams.append(`${key}[]`, '');
        }
      } else {
        urlParams.append(key, param.toString());
      }
    }
  });

  return `?${urlParams.toString()}`;
}

export function extractNavigationLinksFromNavComponent(
  navigationComponent: {
    properties: {
      categories?: {
        items: {
          routes?: { route_link: string }[];
          hide_on?: Array<'Large' | 'Small'>;
          route_link: string;
        }[];
      }[];
    };
  },
  currentScreenSize: LogicContext['currentScreenSize']
): string[] {
  if (!navigationComponent) return [];

  return navigationComponent.properties.categories
    ? navigationComponent.properties.categories
        .map(category =>
          category.items
            .filter(item =>
              item.hide_on && currentScreenSize
                ? !item.hide_on.includes(
                    currentScreenSize === 'wide' ? 'Large' : 'Small'
                  )
                : true
            )
            .map(item =>
              item.routes?.length
                ? item.routes.map(i => i.route_link).concat(item.route_link)
                : item.route_link
            )
        )
        .flat(3)
    : [];
}

export function omitKeys(
  keys: string[],
  data: PartialEventData
): PartialEventData {
  const filteredObj: PartialEventData = {};
  for (const item of Object.entries(data)) {
    if (!keys.includes(item[0])) {
      filteredObj[item[0]] = item[1];
    }
  }
  return filteredObj;
}

export const getCloseStrategyByParams = (
  integration: string,
  closeStrategies: GameCloseStrategies | CloseStrategyEnum[]
) => get(closeStrategies, integration, CloseStrategyEnum.internal);

export const shouldCloseExternally = (strategy: CloseStrategyType) => {
  return strategy === CloseStrategyEnum.external;
};

export function isEmptyObject(obj: object | undefined | null) {
  for (const prop in obj) {
    if (obj.hasOwnProperty(prop)) return false;
  }
  return true;
}

export const delay = (ms: number): Promise<any> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

export const isIFrameMode = (): boolean =>
  config.features.iFrame.mode === 'enabled';

export const hasCorrectJackpotDataStructure = (data: JackpotsSimplifiedType) =>
  has(data, ['section8', 'values']) ||
  has(data, ['vegasmillions', 'values']) ||
  has(data, ['williamHill', 'values']);

/*
  Function checks whether the data fetched from Jackpots API
  and from the application state are the same,
  ignores differences in the 'timestamp' value.
*/
export const isEqualJackpotsData = (
  jackpots: JackpotsSimplifiedType,
  jackpotsNew: JackpotsSimplifiedType
) => {
  if (isEqual(jackpots, jackpotsNew)) return true;

  if (
    !hasCorrectJackpotDataStructure(jackpots) ||
    !hasCorrectJackpotDataStructure(jackpotsNew)
  ) {
    return false;
  }

  const currentData = Object.values(jackpots).map(el => {
    return omitKeys(['timestamp'], el);
  });
  const newData = Object.values(jackpotsNew).map(el => {
    return omitKeys(['timestamp'], el);
  });

  return isEqual(currentData, newData);
};

export const extractRecentlyPlayedType = (
  controllersData?: ControllersDataItem
) => {
  if (controllersData && controllersData.components) {
    const { components } = controllersData;
    const rpCmp = components.find(([id, cmp]) => cmp.type === 'RecentlyPlayed');
    const cmpType = rpCmp ? rpCmp[1]?.properties?.type : '';
    const gamesNumberLimitFromCMS = rpCmp
      ? rpCmp[1]?.properties?.game_number_limit
      : 0;

    return { cmpType, gamesNumberLimitFromCMS };
  }
  return { cmpType: '', gamesNumberLimitFromCMS: 0 };
};

export const toEachWordFirstUpperCase = (phrase: string = '') =>
  phrase
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

// TODO - update to use only special_routes when all from [GT-3242] finished
export const getSpecialRoutePath = (name: FunctionalitiesEnum): string => {
  const path =
    (config.special_routes && config.special_routes[name]) ||
    config.functionalities[name]?.path;

  // TODO - after using only special_routes it will always be an array
  return Array.isArray(path) ? path[path.length - 1] : path;
};

export const isXsellVariant = () =>
  [VariantValues.WhUkXsell, VariantValues.WhEsXsell].includes(
    process.env.REACT_APP_VARIANT
  );
